import type { GameCatalogEntry, GameStatistics } from '@perplexicon/api-client';

import { Icon } from '../../../components/index.js';
import { buildGameCardData } from '../build-game-card-data.js';
import { GameProfileStatsCard } from './GameProfileStatsCard.js';
import { ProfileSectionHeading } from './ProfileSectionHeading.js';

/**
 * The "Gameplay" section under the profile hero (APP_SPEC §13.3) — one
 * reusable `GameProfileStatsCard` per game the player has statistics for.
 * Two equal columns on desktop, stacked below that so each card keeps a
 * full-width illustrated banner and a legible stat panel. Not titled "Game
 * statistics" or "Gameplay statistics".
 *
 * The heading and the cards sit inside one restrained boundary — a lighter
 * parchment field behind a single fine gold line — so Gameplay reads as a
 * composed region of the page rather than a heading with cards floating
 * under it.
 */
export interface GameplaySectionProps {
  gameStatistics: GameStatistics[];
  catalog: GameCatalogEntry[];
}

export function GameplaySection({ gameStatistics, catalog }: GameplaySectionProps) {
  // Render the cards in the same order the game catalog uses (a platform
  // ordering, not a per-game rule — CLAUDE.md §2), so a game sits in the
  // same position here as everywhere else in the app. Games with no catalog
  // entry sort last, keeping their incoming order.
  const catalogOrder = new Map(catalog.map((game, index) => [game.gameId, index]));
  const orderKey = (id: string) => catalogOrder.get(id) ?? Number.MAX_SAFE_INTEGER;
  const cards = buildGameCardData(gameStatistics, catalog).sort(
    (a, b) => orderKey(a.id) - orderKey(b.id),
  );
  if (cards.length === 0) return null;

  return (
    <section
      aria-label="Gameplay"
      className="rounded-lg border border-border bg-parchment p-5 shadow-[inset_0_1px_0_rgb(255_255_255/0.7),0_1px_3px_rgb(75_46_34/0.1)] tablet:p-6"
    >
      <ProfileSectionHeading
        title="Gameplay"
        className="mb-5"
        icon={<Icon name="activeGames" className="h-9 w-9 shrink-0 tablet:h-10 tablet:w-10" />}
      />
      {/* `items-start`: each card is exactly as tall as the game it describes.
          Cards used to stretch to a shared baseline, and the surplus was passed
          down through the card into its stat region — the theory being that a
          two-metric game would spend it on roomier cells. In practice a
          two-metric game does not have two rows to spend it on: Secret
          Number's single row of two stats was inflated to the height of Word
          Rush's *six*, which reads as a blank parchment field with a pair of
          stats floating in the middle of it, not as a roomier layout.

          A game's card should be the size of its own content. Word Rush is
          taller because 3×2 is taller than 1×2; that difference is information
          about the games, and evening it out cost the shorter card its
          composition. */}
      <div className="grid gap-5 desktop:grid-cols-2 desktop:items-start">
        {cards.map((game) => (
          <GameProfileStatsCard key={game.id} game={game} />
        ))}
      </div>
    </section>
  );
}
